import {
  KnownHubDtoType,
  KnownProjectDtoProjectType,
  KnownProjectModuleSettingsDtoName,
  KnownSettingsNodeDtoType
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { Add } from '../../../ui/components/pure/icons/Add.icon';
import { AccessControl } from '../../../ui/components/pure/icons/AccessControl.icon';
import { HubTypeKiosklessSpacesHub } from '../../../ui/components/pure/icons/HubType_KiosklessSpacesHub.icon';
import { HubTypeLockersHub } from '../../../ui/components/pure/icons/HubType_LockersHub.icon';
import { HubTypeSpacesHub } from '../../../ui/components/pure/icons/HubType_SpacesHub.icon';
import { ProjectModule } from '../../../ui/components/pure/icons/ProjectModule.icon';
import { ProjectTypeBusiness } from '../../../ui/components/pure/icons/ProjectType_Business.icon';
import { ProjectTypeCommercial } from '../../../ui/components/pure/icons/ProjectType_Commercial.icon';
import { ProjectFolder } from '../../../ui/components/pure/icons/ProjectType_Folder.icon';
import { ProjectTypeHubs } from '../../../ui/components/pure/icons/ProjectType_Hubs.icon';
import { ProjectTypePrecinct } from '../../../ui/components/pure/icons/ProjectType_Precinct.icon';
import { ProjectTypeProject } from '../../../ui/components/pure/icons/ProjectType_Project.icon';
import { ProjectTypeResidential } from '../../../ui/components/pure/icons/ProjectType_Residential.icon';
import { ProjectTypeRetailer } from '../../../ui/components/pure/icons/ProjectType_Retailer.icon';
import { ProjectTypeStore } from '../../../ui/components/pure/icons/ProjectType_Store.icon';
import { SVGIconComponent } from '../../../ui/components/pure/icons/types';
import { ISettingsNode } from './build-settings-tree';
import { projectModuleToIconMap } from '../../deliveries/maps/project-module-to-icon.map';
import { HubLayout } from '../../../ui/components/pure/icons/HubLayout.icon';
import { RegistrationPage } from '../../../ui/components/pure/icons/RegistrationPage.icon';
import { ProjectTypeLink } from '../../../ui/components/pure/icons/ProjectType_Link.icon';
import { All } from '../../../ui/components/pure/icons/All.icon';
import { ProjectTypeRegion } from '../../../ui/components/pure/icons/ProjectType_Region.icon';
import { Tower } from '../../../ui/components/pure/icons/Tower.icon';
import { Space } from '../../../ui/components/pure/icons/Space.icon';
import { Kiosk } from '../../../ui/components/pure/icons/Kiosk.icon';
import { ProjectTypeTheme } from '../../../ui/components/pure/icons/ProjectType_Theme.icon';
import { CSSProperties } from 'react';
import { LockerXS } from '../../../ui/components/pure/icons/LockerXS.icon';
import { LockerS } from '../../../ui/components/pure/icons/LockerS.icon';
import { LockerM } from '../../../ui/components/pure/icons/LockerM.icon';
import { LockerL } from '../../../ui/components/pure/icons/LockerL.icon';
import { LockerXL } from '../../../ui/components/pure/icons/LockerXL.icon';
import { LockerXXXL } from '../../../ui/components/pure/icons/LockerXXXL.icon';
import { LockerDC } from '../../../ui/components/pure/icons/LockerDC.icon';
import { LockerMR } from '../../../ui/components/pure/icons/LockerMR.icon';
import { LockerLR } from '../../../ui/components/pure/icons/LockerLR.icon';
import { LockerTL } from '../../../ui/components/pure/icons/LockerTL.icon';
import { LockerXLH } from '../../../ui/components/pure/icons/LockerXLH.icon';

export class SettingsMapper {
  static NodeTypeToCreateNodeLabel(nodeType: KnownSettingsNodeDtoType): string {
    switch (nodeType) {
      case KnownSettingsNodeDtoType.Project:
        return 'New child project';
      case KnownSettingsNodeDtoType.ProjectModule:
        return 'New project module';
      case KnownSettingsNodeDtoType.Hub:
        return 'New hub';
      case KnownSettingsNodeDtoType.AccessControl:
        return 'New access control';
      case KnownSettingsNodeDtoType.ProjectLinkFolder:
        return 'New relationship';
      default:
        return 'New child node';
    }
  }

  static NodeTypeToIconStyle(node: ISettingsNode): CSSProperties | undefined {
    switch (node.type) {
      case KnownSettingsNodeDtoType.Locker:
        return { height: 18, width: 18 };
      default:
        return undefined;
    }
  }

  static NodeTypeToIcon(node: ISettingsNode): SVGIconComponent {
    if (node.isPlaceholder) {
      return Add;
    }

    switch (node.type) {
      case KnownSettingsNodeDtoType.Project:
        switch (node.subType) {
          case KnownProjectDtoProjectType.Project:
            return ProjectTypeProject;
          case KnownProjectDtoProjectType.ResidentialBuilding:
            return ProjectTypeResidential;
          case KnownProjectDtoProjectType.CommercialBuilding:
            return ProjectTypeCommercial;
          case KnownProjectDtoProjectType.Precinct:
            return ProjectTypePrecinct;
          case KnownProjectDtoProjectType.Retailer:
            return ProjectTypeRetailer;
          case KnownProjectDtoProjectType.Store:
            return ProjectTypeStore;
          case KnownProjectDtoProjectType.Business:
            return ProjectTypeBusiness;
          case KnownProjectDtoProjectType.Region:
            return ProjectTypeRegion;
          default:
            return ProjectFolder;
        }
      case KnownSettingsNodeDtoType.ProjectModule:
        if (!node.subType) return ProjectModule;
        return projectModuleToIconMap[node.subType as KnownProjectModuleSettingsDtoName];
      case KnownSettingsNodeDtoType.Group:
        return ProjectFolder;
      case KnownSettingsNodeDtoType.GroupsManagedBy:
        return ProjectTypeLink;
      case KnownSettingsNodeDtoType.AccessControl:
        return AccessControl;
      case KnownSettingsNodeDtoType.Hubs:
        return ProjectTypeHubs;
      case KnownSettingsNodeDtoType.Hub:
        return SettingsMapper.HubTypeToIcon(node.subType);
      case KnownSettingsNodeDtoType.HubLayout:
        return HubLayout;
      case KnownSettingsNodeDtoType.Tower:
        return Tower;
      case KnownSettingsNodeDtoType.Locker:
        return SettingsMapper.LockerSizeNameToIcon(node.subType);
      case KnownSettingsNodeDtoType.ProjectModulesFolder:
        return ProjectModule;
      case KnownSettingsNodeDtoType.ProjectLinkFolder:
        return ProjectTypeLink;
      case KnownSettingsNodeDtoType.RegistrationPage:
        return RegistrationPage;
      case KnownSettingsNodeDtoType.HubAllocations:
        return All;
      case KnownSettingsNodeDtoType.Kiosk:
        return Kiosk;
      case KnownSettingsNodeDtoType.KioskTheme:
        return ProjectTypeTheme;
      default:
        return ProjectFolder;
    }
  }

  static ProjectTypeToExternalDeliveryContainerFieldLabel(projectType?: KnownProjectDtoProjectType) {
    if (!projectType) return '';
    const textReplacement = SettingsMapper.ProjectTypeToExternalDeliveryContainerFieldLabelTextReplacement(projectType);
    return `Lockers/Spaces for ${textReplacement} deliveries`;
  }

  static HubTypeToIcon(subType?: string): SVGIconComponent {
    if (!subType) return ProjectFolder;
    switch (subType) {
      case KnownHubDtoType.LockersHub:
      case KnownHubDtoType.LockersHubExternal:
        return HubTypeLockersHub;
      case KnownHubDtoType.SpacesHub:
        return HubTypeSpacesHub;
      case KnownHubDtoType.KiosklessSpacesHub:
        return HubTypeKiosklessSpacesHub;
      default:
        return ProjectFolder;
    }
  }

  static LockerSizeNameToIcon(subType?: string): SVGIconComponent {
    if (!subType) return Space;
    switch (subType) {
      case 'XS':
        return LockerXS;
      case 'S':
        return LockerS;
      case 'M':
        return LockerM;
      case 'L':
        return LockerL;
      case 'XL':
        return LockerXL;
      case 'XXXL':
        return LockerXXXL;
      case 'DC':
        return LockerDC;
      case 'MF':
        return LockerMR;
      case 'LF':
        return LockerLR;
      case 'TL':
        return LockerTL;
      case 'XLH':
        return LockerXLH;
      default:
        return Space;
    }
  }

  static ProjectTypeToInternalDeliveryContainerFieldLabel(projectType?: KnownProjectDtoProjectType) {
    if (!projectType) return '';
    const textReplacement = SettingsMapper.ProjectTypeToInternalDeliveryContainerFieldLabelTextReplacement(projectType);
    return `Lockers/Spaces for ${textReplacement} deliveries`;
  }

  private static ProjectTypeToExternalDeliveryContainerFieldLabelTextReplacement(
    projectType: KnownProjectDtoProjectType
  ) {
    switch (projectType) {
      case KnownProjectDtoProjectType.ResidentialBuilding:
      case KnownProjectDtoProjectType.CommercialBuilding:
      case KnownProjectDtoProjectType.Business:
        return 'courier';
      case KnownProjectDtoProjectType.Retailer:
      case KnownProjectDtoProjectType.Store:
        return 'staff';
      default:
        return '';
    }
  }

  private static ProjectTypeToInternalDeliveryContainerFieldLabelTextReplacement(
    projectType: KnownProjectDtoProjectType
  ) {
    switch (projectType) {
      case KnownProjectDtoProjectType.ResidentialBuilding:
        return 'occupant';
      case KnownProjectDtoProjectType.Business:
        return 'staff';
      case KnownProjectDtoProjectType.CommercialBuilding:
      case KnownProjectDtoProjectType.Retailer:
      case KnownProjectDtoProjectType.Store:
        return 'customer';
      default:
        return '';
    }
  }

  static ProjectTypeToProjectModuleLockedStatus(projectType?: KnownProjectDtoProjectType): boolean {
    switch (projectType) {
      case KnownProjectDtoProjectType.Project:
      case KnownProjectDtoProjectType.Portfolio:
      case KnownProjectDtoProjectType.Precinct:
        return true;
      default:
        return false;
    }
  }

  static ProjectTypeToProjectModuleLockedMessage(projectType?: KnownProjectDtoProjectType): string | null {
    switch (projectType) {
      case KnownProjectDtoProjectType.Project:
      case KnownProjectDtoProjectType.Portfolio:
      case KnownProjectDtoProjectType.Precinct:
        return `There are no modules that support the project type '${SettingsMapper.ProjectTypeToDisplayName(
          projectType
        )}'. You may need to update to the correct project type before you can continue.`;
      default:
        return null;
    }
  }

  private static ProjectTypeToDisplayName(projectType: KnownProjectDtoProjectType): string {
    switch (projectType) {
      case KnownProjectDtoProjectType.Project:
        return 'Project';
      case KnownProjectDtoProjectType.Portfolio:
        return 'Portfolio';
      case KnownProjectDtoProjectType.Precinct:
        return 'Precinct';
      case KnownProjectDtoProjectType.Retailer:
        return 'Retailer';
      case KnownProjectDtoProjectType.Store:
        return 'Store';
      case KnownProjectDtoProjectType.Business:
        return 'Business';
      case KnownProjectDtoProjectType.CommercialBuilding:
        return 'Commercial Building';
      case KnownProjectDtoProjectType.ResidentialBuilding:
        return 'Residential Building';
      case KnownProjectDtoProjectType.Network:
        return 'Network';
      case KnownProjectDtoProjectType.Region:
        return 'Region';
      case KnownProjectDtoProjectType.Carrier:
        return 'Carrier';
      default:
        return 'Project';
    }
  }
}
